import type { OptimizationSuggestion } from "@/client/pages/dashboard/ai-optimization/review";

import type { ComponentTotals, SuggestionGains } from "./coach-attributed-score";
import { buildCoachEditableFields, listSkillNames } from "./coach-editable-fields";
import { distributeGains } from "./coach-gain-distribution";
import {
  flattenResumeText,
  normalizeKeywordList,
  resolveKeywordCoverage,
} from "./coach-live-score";
import { type CoachPlan, requestCoachPlan } from "./coach-plan";
import { classifySkillAdditions, type SkillAddition } from "./coach-skill-additions";
import {
  captureOriginalValues,
  dropNoOpSuggestions,
  type OriginalFieldValues,
} from "./original-content";
import { coachResumeContentKey } from "./saved-coach-plan";
import { verifySuggestions } from "./verify-suggestions";

/**
 * THE optimization pipeline. Both entry points run this one, so neither can get a different AI.
 *
 * WHY IT EXISTS. `/dashboard/ai-optimization` used to call its own older prompt
 * (`optimizeResumeForJob`) while the LMS handoff called `requestCoachPlan`. Same screen afterwards,
 * visibly different results: the dashboard produced plain rewrites with no attributed gains, no skill
 * additions, and no ability to retitle a skill — because it built its editable fields with the base
 * `buildEditableResumeFields`, which never exposes a skill's `name`. It also skipped verification and
 * no-op filtering entirely, so it could hand the builder a suggestion aimed at a fieldPath the résumé
 * does not have.
 *
 * Every guard in here exists because of a specific failure, and they are ordered deliberately:
 *
 *   1. `buildCoachEditableFields` — the coach variant, which also exposes skill NAMES. `skillOverlap`
 *      is the heaviest résumé-movable component (weight 30) and without this it has almost no lever.
 *   2. `captureOriginalValues` — snapshot BEFORE anything is accepted. This is what Reject restores;
 *      `suggestion.previousText` cannot be trusted for it, being re-derived from the current résumé on
 *      every run, so after one accepted run it holds the PREVIOUS run's rewrite.
 *   3. keyword coverage — narrows the LMS's missing-skill list to what this résumé does not already
 *      cover, so the model is not asked to add keywords that are in there.
 *   4. `requestCoachPlan` — scores and plans in one call, attributing a gain per suggestion.
 *   5. `verifySuggestions` — the parser validates SHAPE only. An invented fieldPath would be written
 *      into the document by lodash `_set`, and a `previousText` aimed at absent text would silently
 *      overwrite real content.
 *   6. `dropNoOpSuggestions` — rewrites that restore text already present. The student accepts,
 *      nothing changes, the score does not move, and the feature looks broken.
 *   7. `classifySkillAdditions` — evidenced vs unverified, and de-duplicated against listed skills.
 *   8. `distributeGains` — fits the claimed gains into the headroom each component actually has.
 *      LAST on purpose: every filter above frees the headroom its dropped suggestions were
 *      claiming, so normalizing earlier would shrink the survivors to make room for cards the
 *      student never sees. The LMS runs the same function at save time, which is what keeps the
 *      number in the builder and the number on the job card identical.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

export type CoachPlanStage = "analyze" | "match" | "suggestions";

export type BuildCoachPlanInput = {
  jobDescription: string;
  jobUuid?: string;
  /** The résumé being optimized, as stored. Must be the persisted résumé, not an upload preview. */
  resumeData: unknown;
  /**
   * Baseline sub-scores from the LMS, so the model calibrates its gains against real numbers.
   * Zeros for the dashboard flow, which has no job record — the model then self-assesses.
   */
  baseline: ComponentTotals;
  /**
   * Component weights from the LMS, so the model knows which lever is heavier and the prompt
   * can state what a sub-score point is worth in the final score.
   *
   * The dashboard flow passes `RESUME_FIT_WEIGHTS`, the same weights its own session uses.
   */
  weights: Record<string, number>;
  /** Skills the LMS says the score is penalising. Empty for the dashboard flow. */
  missingSkills?: string[];
  /** Skills already credited, so a suggestion is not spent restating them. */
  presentSkills?: string[];
  /** Progress reporting, so a caller with a staged loader can narrate without owning the steps. */
  onStage?: (stage: CoachPlanStage, detail: string) => void;
};

export type BuildCoachPlanResult = {
  suggestions: OptimizationSuggestion[];
  gains: SuggestionGains;
  skillAdditions: SkillAddition[];
  originalValues: OriginalFieldValues;
  /**
   * The model's own read of where the résumé currently stands on the two editable components.
   *
   * The LMS flow ignores this — it has real sub-scores from the scorer and those are authoritative. The
   * dashboard flow has no job record, so this is the only baseline available to it, and it is what lets
   * that flow show the same score donut instead of a different panel.
   */
  assessed: ComponentTotals;
  /** The model's read of the JD. Returned for logging — a bad plan is usually a bad reading. */
  jobRequires: string[];
};

/** Raised when the résumé has nothing the optimizer is allowed to touch. */
export class EmptyResumeError extends Error {}
/** Raised when the plan came back unreadable, as opposed to empty. */
export class UnreadablePlanError extends Error {}
/**
 * Raised when nothing survived the guards above.
 *
 * `droppedAsNoOps` separates two outcomes that need different words. If the model proposed changes and
 * they were all filtered as already-present, the résumé genuinely is as aligned as it honestly can be
 * and telling the student to "try again" sends them round a loop that cannot succeed. If it proposed
 * nothing at all, retrying is the right advice.
 */
export class NothingToImproveError extends Error {
  readonly droppedAsNoOps: boolean;

  constructor(message: string, droppedAsNoOps: boolean) {
    super(message);
    this.droppedAsNoOps = droppedAsNoOps;
  }
}

export const buildCoachPlan = async ({
  jobDescription,
  jobUuid,
  resumeData,
  baseline,
  weights,
  missingSkills = [],
  presentSkills = [],
  onStage,
}: BuildCoachPlanInput): Promise<BuildCoachPlanResult> => {
  const editableFields = buildCoachEditableFields(resumeData as Record<string, unknown>);

  if (editableFields.length === 0) {
    throw new EmptyResumeError(
      "This resume has no summary or descriptions for the AI to improve. Add some detail first, then try again.",
    );
  }

  const originalValues = captureOriginalValues(editableFields);

  onStage?.(
    "analyze",
    `${editableFields.length} editable field${editableFields.length === 1 ? "" : "s"}`,
  );

  const resumeText = flattenResumeText(resumeData);
  const { missing } = resolveKeywordCoverage(resumeText, [...missingSkills, ...presentSkills]);
  const scoredGaps = normalizeKeywordList(missing);

  onStage?.(
    "match",
    scoredGaps.length
      ? `${scoredGaps.length} keyword gap${scoredGaps.length === 1 ? "" : "s"} found`
      : "No keyword gaps found",
  );

  onStage?.("suggestions", "");

  let plan: CoachPlan;
  try {
    plan = await requestCoachPlan({
      jobDescription,
      jobUuid,
      resumeContent: coachResumeContentKey(resumeData),
      editableFields,
      baseline,
      weights,
      missingSkills: scoredGaps,
      presentSkills: normalizeKeywordList(presentSkills),
    });
  } catch {
    throw new UnreadablePlanError("The AI response couldn't be read. Please try again.");
  }

  const verification = verifySuggestions(plan.suggestions, editableFields);
  if (verification.rejected.length > 0) {
    console.warn(
      `[CoachResume] discarded ${verification.rejected.length} suggestion(s) that did not match the resume:`,
      verification.rejected.map((entry) => entry.reason),
    );
  }

  const { actionable, noOps } = dropNoOpSuggestions(
    verification.accepted,
    originalValues,
    scoredGaps,
  );
  if (noOps.length > 0) {
    console.info(
      `[CoachResume] dropped ${noOps.length} cosmetic suggestion(s) — already present, or a reword ` +
        `adding nothing the scorer reads`,
    );
  }

  const skillPlan = classifySkillAdditions(
    plan.addSkills,
    resumeText,
    listSkillNames(resumeData as Record<string, unknown>),
  );

  if (skillPlan.rejected.length > 0) {
    console.info(
      `[CoachResume] dropped ${skillPlan.rejected.length} duplicate skill proposal(s):`,
      skillPlan.rejected.map((entry) => entry.reason),
    );
  }
  console.info(
    `[CoachResume] skills proposed — ${skillPlan.evidenced.length} evidenced by the resume, ` +
      `${skillPlan.unverified.length} for the student to confirm`,
  );

  if (actionable.length === 0 && skillPlan.accepted.length === 0) {
    throw new NothingToImproveError(
      noOps.length > 0
        ? "Your resume is already as aligned with this job as it honestly can be. The remaining gaps need experience it doesn't yet show, so there's nothing safe to rewrite."
        : "The AI didn't return any usable suggestions this time. Please try again.",
      noOps.length > 0,
    );
  }

  /**
   * ── 8. Fit the claimed gains into the headroom that exists ────────────────────────────
   *
   * LAST, and that position is the point. Everything above removes suggestions — unverifiable
   * fieldPaths, no-ops, duplicate skills — and every removal frees the headroom that
   * suggestion was claiming. Normalizing earlier would shrink the survivors to make room for
   * cards the student never sees.
   *
   * The model cannot know how much room is left on a component, so it over-claims against a
   * résumé that is already strong somewhere. `projectTotalScore` used to absorb that silently
   * by clamping the sub-score at 100 — the cards kept advertising the full "+N", the
   * "accept everything" figure was computed from the same over-claim, and the score the
   * student ended up with was lower than the one they had been watching.
   *
   * The LMS runs the identical function over the same plan when the student saves, so the
   * builder's number and the stored number are the same number.
   */
  const distribution = distributeGains({
    gains: plan.gains,
    suggestions: actionable,
    skillAdditions: skillPlan.accepted,
    baseline,
  });

  const rescaled = Object.entries(distribution.scaled).filter(([, factor]) => factor < 1);
  if (rescaled.length > 0) {
    console.info(
      `[CoachResume] claimed gains exceeded the available headroom and were scaled back: ` +
        rescaled
          .map(([component, factor]) => `${component} ×${(factor ?? 1).toFixed(2)}`)
          .join(", ") +
        `. Baseline roleDomainFit=${Math.round(baseline.roleDomainFit)} ` +
        `skillOverlap=${Math.round(baseline.skillOverlap)}.`,
    );
  }
  if (distribution.dropped.length > 0) {
    console.info(
      `[CoachResume] ${distribution.dropped.length} suggestion(s) now carry no score — their ` +
        `component has no room left. Still shown, since a rewrite can be worth making to a ` +
        `human reader without being worth points.`,
    );
  }

  return {
    suggestions: distribution.suggestions,
    gains: distribution.gains,
    skillAdditions: distribution.skillAdditions,
    originalValues,
    assessed: plan.assessed,
    jobRequires: plan.jobRequires,
  };
};
